import { useCallback, useRef } from 'react'
import AppStore from '@v4/store/appStore'
import QuizQuestionsSelector from '@v4/utils/selectors/QuizQuestionsSelector'
import QuestionsBuilderSelector from '@v4/utils/selectors/QuestionsBuilderSelector'
import QuestionsBuilderModel from '@v4/store/builderStore/models/QuestionsBuilder.model'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageBuilderModel from '@v4/store/builderStore/models/PageBuilder.model'
import useStyles from './OptionButton.styled'

export const useOptionButton = props => {
	const {
		className,
		id,
		disabled,
		loading,
		value,
		onClick,
		onChange,
		onDelete,
		deletable,
		type,
		checked,
		draggable,
		autoFocus,
		placeholder,
	} = props
	const classes = useStyles(props)
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const deletingOptionIds = QuizQuestionsSelector.useDeletingOptionIds()
	const { setScrollingMenu, setActiveOptionId, setQuestionTab } = QuestionsBuilderSelector.useActions()
	const locked = ScorecardSelector.useIsQuestionsLocked()
	const { setSidebarMode } = PageBuilderSelector.useActions()

	const valueRef = useRef('')
	valueRef.current = value

	const handleChange = useCallback(
		option => {
			onChange({ id, option })
		},
		[onChange, id],
	)

	const handleLabelFocus = useCallback(() => {
		const pattern = /^Answer \d+$/g

		if (pattern.test(valueRef.current)) {
			handleChange('')
		}

		setScrollingMenu?.(true)
		setActiveOptionId(id)
		setSidebarMode(PageBuilderModel.SIDEBAR_MODE.QUESTIONS)
		setQuestionTab(QuestionsBuilderModel.QUESTION_TAB.LOGIC)
	}, [handleChange, id, setActiveOptionId, setQuestionTab, setScrollingMenu, setSidebarMode])

	const handleClick = useCallback(
		event => {
			if (isBuilderMode && isQuestionBuilderMode && event.detail) {
				const editorElement = event.target.querySelector('.mce-content-body')
				if (editorElement) {
					editorElement.focus()
					setTimeout(() => {
						try {
							document.execCommand('selectAll', false, null)
							document.getSelection().collapseToEnd()
							// eslint-disable-next-line no-empty
						} catch (e) {}
					}, 0)
				}
			}
			if (isBuilderMode) {
				return
			}
			onClick({ optionId: id })
		},
		[isBuilderMode, isQuestionBuilderMode, onClick, id],
	)

	const handleKeyUp = useCallback(
		({ key }) => {
			if (key === 'Enter' && type !== 'checkbox' && !isBuilderMode) {
				onClick({ optionId: id })
			}
		},
		[id, isBuilderMode, onClick, type],
	)

	const handleKeyDown = useCallback(
		event => {
			if (event.key === 'Enter' && type === 'checkbox') {
				event.preventDefault()
			}
		},
		[type],
	)

	const handleDelete = useCallback(() => {
		onDelete(id)
	}, [id, onDelete])

	const editable = !locked && isBuilderMode && isQuestionBuilderMode

	return [
		{
			className,
			disabled,
			loading,
			children: value,
			editable,
			deletable: deletable && editable,
			classes,
			type,
			checked: type === 'checkbox' ? checked : undefined,
			variant: type === 'checkbox' ? 'outlined' : undefined,
			draggable,
			deleting: _.includes(deletingOptionIds, id),
			autoFocus,
			placeholder,
		},
		{
			handleClick,
			handleChange,
			handleDelete,
			handleKeyDown,
			handleKeyUp,
			handleLabelFocus: editable ? handleLabelFocus : undefined,
		},
	]
}
